// A faithful port of the polling state machine in the autograding-regenerate-button directive. The
// machine is long and branchy by nature: start, poll, terminal and active statuses, retries after a
// transient error, resume on mount, and cleanup. A split would hide the mapping to the original.
/* eslint-disable max-lines-per-function, complexity */
import { useErrorLogService } from 'ErrorLogging';
import { useCallback, useEffect, useRef, useState } from 'react';
import { notifyToast } from './notifyToast';
import { type UseAutogradingReturn } from './ProjectFieldsModal.types';

const POLL_INTERVAL_MS = 5000;
// The delay before the first poll. It gives a worker time to pick up the job.
const INITIAL_POLL_DELAY_MS = 3000;
// A little longer than the 15-minute server timeout.
const MAX_POLL_DURATION_MS = 20 * 60 * 1000;
const MAX_CONSECUTIVE_ERRORS = 3;
const TERMINAL_STATUSES = ['completed', 'failed', 'timed_out', 'cancelled'];
// 'preprocessing' counts as active, so the poll stays alive while the server transcribes the
// presentation. The Regrade button then keeps its spinner instead of looking stalled.
const ACTIVE_STATUSES = ['pending', 'in_progress', 'enqueued', 'preprocessing'];

type UseAutogradingArgs = {
    submissionId: string | null;
    autoGradingEnabled: boolean;
    hasSubmission: boolean;
    hasScoreSet: boolean;
    serverStatus: string | null;
    // The presentation transcript lives on the submission, not on projectProgress. It goes up
    // separately, and the parent form applies it when the grade is not a regenerated presentation
    // grade.
    onCompleted: (
        gradedScore: number,
        gradedFeedback: string | null,
        presentationTranscript: string | null,
        skippedFormatTypes: string[],
    ) => void;
    // Runs once the server accepts a regrade, so the parent can null the stale provisional score
    // and feedback for the grading window. A refused or failed start does not call it.
    onStarted: () => void;
};

function statusMessage(status: string | null): string {
    switch (status) {
        case 'enqueued':
        case 'pending':
            return 'Enqueued for grading...';
        case 'in_progress':
            return 'Grading in progress...';
        case 'preprocessing':
            return 'Preprocessing...';
        case 'completed':
            return 'Grading completed!';
        case 'failed':
            return 'Autograding failed';
        case 'timed_out':
            return 'Autograding timed out';
        case 'cancelled':
            return 'Autograding cancelled';
        default:
            return '';
    }
}

// Ports the autograding-regenerate-button directive. It starts an autograding session and polls for
// the status. On completion it updates the form's provisional score and feedback. The timers live in
// refs, and unmount clears them.
export function useAutograding({
    submissionId,
    autoGradingEnabled,
    hasSubmission,
    hasScoreSet,
    serverStatus,
    onCompleted,
    onStarted,
}: UseAutogradingArgs): UseAutogradingReturn {
    const errorLogService = useErrorLogService();
    const [inProgress, setInProgress] = useState(ACTIVE_STATUSES.includes(serverStatus ?? ''));
    const [status, setStatus] = useState<string | null>(serverStatus);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [errorDetails, setErrorDetails] = useState<string | null>(null);

    const pollTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
    const pollStartTime = useRef(0);
    const consecutiveErrors = useRef(0);
    const activeSubmissionId = useRef<string | null>(
        ACTIVE_STATUSES.includes(serverStatus ?? '') ? submissionId : null,
    );

    const stopPolling = useCallback(() => {
        if (pollTimeout.current) {
            clearTimeout(pollTimeout.current);
            pollTimeout.current = null;
        }
    }, []);

    const poll = useCallback(async () => {
        const id = activeSubmissionId.current;
        if (!id) return;

        if (Date.now() - pollStartTime.current > MAX_POLL_DURATION_MS) {
            stopPolling();
            setInProgress(false);
            setErrorMessage('Autograding is taking longer than expected. Please check back later.');
            notifyToast('Autograding is taking longer than expected. Please check back later.', 'warning');
            errorLogService?.notify('Autograding poll timed out on client', null, {
                level: 'warning',
                submission_id: id,
            });
            return;
        }

        try {
            // Use .json so an error response arrives as JSON, not as an HTML string body. This
            // matches the app-wide convention and the original directive.
            const response = await fetch(`/api/autograding_sessions/status.json?project_submission_id=${id}`);
            if (!response.ok) {
                const errData = await response.json().catch(() => ({}));
                throw Object.assign(new Error('status check failed'), {
                    status: response.status,
                    serverMessage: errData.message ?? null,
                });
            }
            const data = await response.json();
            consecutiveErrors.current = 0;
            setStatus(data.status);
            if (data.error_message) setErrorMessage(data.error_message);
            if (data.error_details) setErrorDetails(data.error_details);

            if (TERMINAL_STATUSES.includes(data.status)) {
                stopPolling();
                setInProgress(false);
                // The original toasted every terminal status: success on completion, warning otherwise.
                // The grader can be scrolled away from the button, so an inline message is not enough.
                notifyToast(
                    data.status === 'completed' ? 'Autograding completed successfully!' : statusMessage(data.status),
                    data.status === 'completed' ? 'success' : 'warning',
                );
                if (data.status === 'completed' && data.graded_score != null) {
                    onCompleted(
                        data.graded_score,
                        data.graded_feedback ?? null,
                        data.presentation_transcript ?? null,
                        data.skipped_format_types ?? [],
                    );
                }
            } else {
                pollTimeout.current = setTimeout(poll, POLL_INTERVAL_MS);
            }
        } catch (error) {
            const httpStatus = (error as { status?: number }).status;
            const serverMessage = (error as { serverMessage?: string | null }).serverMessage ?? null;
            errorLogService?.notify('Autograding status poll failed', error, {
                level: 'warning',
                submission_id: id,
                error_status: httpStatus,
            });
            if (httpStatus === 401 || httpStatus === 404 || httpStatus === 403) {
                stopPolling();
                setInProgress(false);
                // A 404 or 403 shows the server's message when there is one, as the original did.
                // Otherwise it shows a generic not-found or unauthorized string. A 401 always shows
                // the session-expired prompt.
                setErrorMessage(
                    httpStatus === 401
                        ? 'Session expired. Please refresh the page.'
                        : serverMessage || 'Autograding session not found or unauthorized',
                );
                return;
            }
            consecutiveErrors.current += 1;
            if (consecutiveErrors.current >= MAX_CONSECUTIVE_ERRORS) {
                stopPolling();
                setInProgress(false);
                setErrorMessage('Unable to check autograding status. Please try again later.');
                notifyToast('Unable to check autograding status. Please try again later.', 'warning');
                return;
            }
            pollTimeout.current = setTimeout(poll, POLL_INTERVAL_MS);
        }
    }, [errorLogService, onCompleted, stopPolling]);

    const startPolling = useCallback(() => {
        stopPolling();
        pollStartTime.current = Date.now();
        consecutiveErrors.current = 0;
        pollTimeout.current = setTimeout(poll, INITIAL_POLL_DELAY_MS);
    }, [poll, stopPolling]);

    const start = useCallback(() => {
        if (inProgress || hasScoreSet || !submissionId) return;
        if (ACTIVE_STATUSES.includes(status ?? '')) return;

        activeSubmissionId.current = submissionId;
        setInProgress(true);
        setErrorMessage(null);
        setErrorDetails(null);
        setStatus('pending');

        // Use .json so a 409 refusal returns a JSON body to show, not an HTML error page.
        fetch('/api/autograding_sessions/start.json', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ project_submission_id: submissionId }),
        })
            .then(async response => {
                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    throw Object.assign(new Error(errorData.message || 'Failed to start autograding'), {
                        status: response.status,
                        serverMessage: errorData.message ?? null,
                    });
                }
                const data = await response.json();
                // Clear the old provisional grade only now, because the server accepted the regrade.
                // The grader then sees no stale data during the poll window, and a refused regrade
                // never changes the displayed grade. The original start-success handler did the same.
                onStarted();
                setStatus(data.status);
                // The original derived the start toast from the returned status.
                notifyToast(statusMessage(data.status) || 'Autograding started', 'success');
                startPolling();
            })
            .catch((error: { status?: number; serverMessage?: string | null }) => {
                // Every start failure shows a danger toast, as the original did. A 409 is an
                // expected refusal the user can act on: the submission is not eligible for a regrade,
                // or a session already exists. A submission becomes ineligible when its formats no
                // longer match the project. So a 409 shows a toast but does not reach Sentry
                // (#17154). Any other failure is unexpected, so it shows a toast and reaches Sentry.
                // The client never touches the provisional grade, so a refused regrade leaves the
                // existing grade in place.
                const isRefusal = error?.status === 409;
                notifyToast(
                    error?.serverMessage ||
                        (isRefusal ? 'This submission is not eligible for regrading.' : 'Failed to start autograding'),
                    'danger',
                );
                if (!isRefusal) {
                    errorLogService?.notify('Failed to start autograding', error, {
                        level: 'error',
                        submission_id: submissionId,
                        error_status: error?.status,
                    });
                }
                setInProgress(false);
                setStatus(null);
                setErrorMessage('Failed to start autograding');
            });
    }, [errorLogService, hasScoreSet, inProgress, onStarted, startPolling, status, submissionId]);

    // Seed on mount, then re-seed whenever the modal swaps in a different submission or a fresh server
    // status. An admin upload replaces submissionData in place and does not remount this hook, so
    // without this the hook keeps the old status and keeps polling the old submission id.
    //
    // The dep list is intentionally narrow. startPolling changes whenever the parent's onCompleted
    // identity changes, and depending on it would reset a regrade that is already running.
    useEffect(() => {
        stopPolling();
        const active = ACTIVE_STATUSES.includes(serverStatus ?? '');
        setStatus(serverStatus);
        setInProgress(active);
        setErrorMessage(null);
        setErrorDetails(null);
        activeSubmissionId.current = active && submissionId ? submissionId : null;
        if (active && submissionId) startPolling();
        return stopPolling;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [submissionId, serverStatus]);

    const isDisabled = inProgress || hasScoreSet || ACTIVE_STATUSES.includes(status ?? serverStatus ?? '');

    let buttonTooltip = '';
    if (inProgress) buttonTooltip = 'Autograding in progress...';
    else if (hasScoreSet) buttonTooltip = 'Cannot regenerate - approved score exists';
    else if (ACTIVE_STATUSES.includes(status ?? serverStatus ?? '')) buttonTooltip = 'Autograding already in progress';

    const visibleError =
        !inProgress && status && status !== 'completed'
            ? (status === 'failed' && 'Autograding failed. Please try again.') ||
              (status === 'timed_out' && 'Autograding timed out. Please try again.') ||
              // A cancelled status uses its own message when the server sent no error_message. The
              // original showed 'Autograding was cancelled.' there.
              (status === 'cancelled' && (errorMessage || 'Autograding was cancelled.')) ||
              errorMessage
            : null;

    return {
        showButton: autoGradingEnabled && hasSubmission,
        isDisabled,
        inProgress,
        buttonTooltip,
        buttonLabel: inProgress ? statusMessage(status ?? serverStatus) : 'Regrade',
        errorMessage: visibleError || null,
        errorDetails,
        status,
        start,
    };
}
